import type { ReactNode } from 'react';

import { AttachmentsProvider } from './attachments-provider';
import { ChatAttachments } from './chat-attachments';
import { ChatAttachmentsPreview } from './chat-attachments-preview';
import { ChatInput } from './chat-input';
import { ChatInputProvider, DragHandlersProvider } from './chat-input-context';
import { ChatInputTextarea } from './chat-input-textarea';
import { ChatInputToolbar } from './chat-input-toolbar';
import { DragOverlay } from './drag-overlay';
import { SendButton } from './send-button';
import { useChatInputSetup } from './use-chat-input-setup';

interface ChatInputContainerProps {
  placeholder?: string;
  className?: string;
  /**
   * Custom toolbar content. If provided, replaces the default toolbar.
   * If not provided, uses default toolbar with attachments and send button.
   */
  toolbar?: ReactNode;
  topToolBar?: ReactNode;
  /**
   * Maximum allowed message length in characters. When set, submission is blocked
   * beyond this limit across every submit path. Undefined means no limit.
   */
  maxLength?: number;
}

/**
 * Default chat input container that sets up context and provides the standard composition.
 * Apps can customize the toolbar by passing a custom toolbar prop.
 *
 * For more advanced customization, use the `useChatInputSetup` hook directly.
 *
 * @example Default usage:
 * ```tsx
 * <ChatInputContainer />
 * ```
 *
 * @example Custom toolbar:
 * ```tsx
 * <ChatInputContainer
 *   toolbar={
 *     <ChatInputToolbar>
 *       <CustomButton />
 *       <SendButton />
 *     </ChatInputToolbar>
 *   }
 * />
 * ```
 *
 * @example Without attachments:
 * ```tsx
 * <ChatInputContainer
 *   toolbar={
 *     <ChatInputToolbar>
 *       <SendButton />
 *     </ChatInputToolbar>
 *   }
 * />
 * ```
 *
 * @example Full customization with hook:
 * ```tsx
 * function MyCustomInput() {
 *   const contextValue = useChatInputSetup();
 *
 *   return (
 *     <ChatInputProvider value={contextValue}>
 *       <ChatInput>
 *         <ChatInputTextarea />
 *         <ChatInputToolbar>
 *           <MyCustomActions />
 *           <SendButton />
 *         </ChatInputToolbar>
 *       </ChatInput>
 *     </ChatInputProvider>
 *   );
 * }
 * ```
 */
export function ChatInputContainer({
  placeholder = 'Ask a question...',
  className,
  toolbar,
  topToolBar,
  maxLength,
}: ChatInputContainerProps) {
  const contextValue = useChatInputSetup({ maxLength });

  const defaultToolbar = (
    <ChatInputToolbar>
      <div className="flex min-w-0 flex-1 items-center gap-1">
        <ChatAttachments className="shrink-0" />
        <ChatAttachmentsPreview />
      </div>
      <SendButton />
    </ChatInputToolbar>
  );

  return (
    <AttachmentsProvider>
      <DragHandlersProvider>
        <ChatInputProvider value={contextValue}>
          {topToolBar}
          <ChatInput className={className}>
            <DragOverlay />
            <ChatInputTextarea placeholder={placeholder} />
            {toolbar ?? defaultToolbar}
          </ChatInput>
        </ChatInputProvider>
      </DragHandlersProvider>
    </AttachmentsProvider>
  );
}
